/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Design Automation team for Inventor
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import React, { Component } from "react";
import { connect } from "react-redux";
import "./drawingsContainer.css";
import Button from "@hig/button";
import {
    getActiveProject,
    getDrawingsList,
    getActiveDrawing,
    downloadProgressShowing,
    downloadProgressTitle,
    downloadUrl,
    downloadFailedShowing,
    errorData
} from "../reducers/mainReducer";
import { getDownloadLink } from "../actions/downloadActions";
import {
    showDownloadProgress,
    showDownloadFailed
} from "../actions/uiFlagsActions";
import { fetchDrawingsList } from "../actions/drawingsListActions";
import { updateActiveDrawing } from "../actions/uiFlagsActions";
import BaseTable, { AutoResizer, Column } from "react-base-table";
import "react-base-table/styles.css";
import ModalDownloadProgress from "./modalDownloadProgress";
import ModalFail from "./modalFail";

const Icon = ({ iconname }) => (
    <div>
        <img src={iconname} alt="" width="16px" height="16px" />
    </div>
);

const iconRenderer = ({ cellData: iconname }) => <Icon iconname={iconname} />;

export const drawingColumns = [
    {
        key: "icon",
        title: "",
        dataKey: "icon",
        cellRenderer: iconRenderer,
        align: Column.Alignment.RIGHT,
        width: 30
    },
    {
        key: "type",
        title: "Rysunki",
        dataKey: "name",
        cellRenderer: ({ rowData }) => rowData.label,
        align: Column.Alignment.LEFT,
        width: 200
    }
];

export class DrawingsContainer extends Component {
    componentDidMount() {
        if (this.props.activeProject) {
            this.props.fetchDrawingsList(this.props.activeProject);
        }
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.activeProject &&
            prevProps.activeProject &&
            this.props.activeProject.id !== prevProps.activeProject.id
        ) {
            this.props.fetchDrawingsList(this.props.activeProject);
        }
    }

    onDownloadPDF() {
        const project = this.props.activeProject;

        if (project?.id) {
            this.props.getDownloadLink(
                "CreateDrawingPdfJob",
                project.id,
                project.hash,
                "Generowanie rysunku w formacie PDF",
                this.props.activeDrawing
            );
        }
    }

    onRowClick(rowData) {
        if (this.props.activeDrawing === rowData.id) {
            return;
        }

        this.props.updateActiveDrawing(rowData.id);
    }

    render() {
        const drawingsList = this.props.activeProject
            ? this.props.drawingsList
            : [];

        const project = this.props.activeProject;

        let data = [];
        if (drawingsList) {
            const shortName = function (name) {
                const onlyName = name.split("\\").pop().split("/").pop();
                return onlyName;
            };

            data = drawingsList.map((drawing) => ({
                id: drawing,
                icon: "document-drawing-24.svg",
                label: shortName(drawing)
            }));
        }

        return (
            <div className="drawingsListContainer">
                <div className="pencilContainer"></div>

                <div className="drawingsList">
                    <AutoResizer>
                        {({ width, height }) => {
                            const newWidth = width;
                            const newHeight = height - 16;

                            return (
                                <BaseTable
                                    className="drawingList drawingsTableCustom"
                                    width={newWidth}
                                    height={newHeight}
                                    columns={drawingColumns}
                                    data={data}
                                    rowEventHandlers={{
                                        onClick: ({ rowData }) => {
                                            this.onRowClick(rowData);
                                        }
                                    }}
                                    rowClassName={({ rowData }) => {
                                        if (rowData.id === this.props.activeDrawing) {
                                            return "drawing-row drawing-selected";
                                        }

                                        return "drawing-row";
                                    }}
                                />
                            );
                        }}
                    </AutoResizer>
                </div>

                <hr className="drawingsListSeparator" />

                <div className="buttonsContainer">
                    <Button
                        className="orangeButton"
                        size="standard"
                        title="Eksport PDF"
                        width="grow"
                        onClick={() => {
                            this.onDownloadPDF();
                        }}
                        disabled={!this.props.activeDrawing}
                    />
                </div>

                {this.props.downloadProgressShowing && (
                    <ModalDownloadProgress
                        open={true}
                        title={this.props.downloadProgressTitle}
                        label={project.id}
                        icon="/Archive.svg"
                        onClose={() => this.props.showDownloadProgress(false)}
                        url={this.props.downloadUrl}
                        statsKey={this.props.activeDrawing}
                    />
                )}

                {this.props.downloadFailedShowing && (
                    <ModalFail
                        open={true}
                        title={`${this.props.downloadProgressTitle} Failed`}
                        contentName="Project:"
                        label={project.id}
                        onClose={() => this.props.showDownloadFailed(false)}
                        errorData={this.props.errorData}
                    />
                )}
            </div>
        );
    }
}

/* istanbul ignore next */
export default connect(
    function (store) {
        const activeProject = getActiveProject(store);
        return {
            activeProject: activeProject,
            downloadProgressShowing: downloadProgressShowing(store),
            downloadProgressTitle: downloadProgressTitle(store),
            downloadFailedShowing: downloadFailedShowing(store),
            downloadUrl: downloadUrl(store),
            errorData: errorData(store),
            activeDrawing: getActiveDrawing(store),
            drawingsList: getDrawingsList(store)
        };
    },
    {
        getDownloadLink,
        fetchDrawingsList,
        updateActiveDrawing,
        showDownloadProgress,
        showDownloadFailed
    }
)(DrawingsContainer);